import React, { useEffect, useState } from 'react';
import { Box, Tab, Tabs } from '@mui/material';
import { useNotify, useTranslate } from 'react-admin';
import { ReportLayout } from '../shared/ReportLayout';
import { ReportFilters } from '../shared/ReportFilters';
import { getDateRangePresets } from '../../../utils/reportHelpers';
import { KIOSK_PAYMENT_METHODS } from '../../transactions/kiosk/refundState';
import { downloadKioskReportCsv, loadKioskReport, loadReportKiosks, loadReportSites } from './kioskReportApi';
import { DailyMatchTable, OverviewTable, PayoutTable, RefundsHealthTable } from './KioskReportTables';
import { KIOSK_REPORT_TABS, type FilterOption, type KioskReportTab, type LoadedKioskReport } from './types';

const toError = (value: unknown): Error => {
  if (value instanceof Error) return value;
  if (typeof value === 'object' && value !== null && 'message' in value) return new Error(String((value as { message: unknown }).message));
  return new Error(String(value));
};

export const KioskReport: React.FC = () => {
  const translate = useTranslate();
  const notify = useNotify();
  const [presets] = useState(() => getDateRangePresets());
  const [tab, setTab] = useState<KioskReportTab>('payout');
  const [startDate, setStartDate] = useState(presets.lastMonth.start);
  const [endDate, setEndDate] = useState(presets.lastMonth.end);
  const [siteId, setSiteId] = useState('');
  const [kioskId, setKioskId] = useState('');
  const [method, setMethod] = useState('');
  const [sites, setSites] = useState<FilterOption[]>([]);
  const [kiosks, setKiosks] = useState<FilterOption[]>([]);
  const [report, setReport] = useState<LoadedKioskReport | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [exporting, setExporting] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);
  const filters = { startDate, endDate, siteId, kioskId, method };

  useEffect(() => {
    loadReportSites().then(setSites).catch(() => notify(translate('reports.kiosk.filtersFailed'), { type: 'error' }));
  }, []);

  useEffect(() => {
    let active = true;
    loadReportKiosks(siteId)
      .then((list) => {
        if (!active) return;
        setKiosks(list);
        setKioskId((current) => (current !== '' && !list.some((kiosk) => kiosk.id === current) ? '' : current));
      })
      .catch(() => {
        if (active) notify(translate('reports.kiosk.filtersFailed'), { type: 'error' });
      });
    return () => {
      active = false;
    };
  }, [siteId]);

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError(null);
    setReport(null);
    loadKioskReport(tab, { startDate, endDate, siteId, kioskId, method })
      .then((body) => {
        if (active) setReport({ tab, body } as LoadedKioskReport);
      })
      .catch((failure) => {
        if (active) setError(toError(failure));
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [tab, startDate, endDate, siteId, kioskId, method, reloadKey]);

  const handleExport = async () => {
    setExporting(true);
    try {
      await downloadKioskReportCsv(tab, filters);
    } catch {
      notify(translate('reports.kiosk.exportFailed'), { type: 'error' });
    } finally {
      setExporting(false);
    }
  };

  return (
    <ReportLayout
      title={translate('reports.kiosk.title')}
      description={translate('reports.kiosk.description')}
      filters={
        <Box sx={{ minWidth: 0 }}>
          <ReportFilters
            startDate={startDate}
            endDate={endDate}
            onStartDateChange={setStartDate}
            onEndDateChange={setEndDate}
            siteId={siteId}
            onSiteIdChange={setSiteId}
            showSiteFilter
            sites={sites}
            kioskId={kioskId}
            onKioskIdChange={setKioskId}
            showKioskFilter
            kiosks={kiosks}
            paymentMethod={method}
            onPaymentMethodChange={setMethod}
            showPaymentMethodFilter
            paymentMethods={KIOSK_PAYMENT_METHODS.map((id) => ({ id, name: translate(`resources.kiosk-transactions.methods.${id}`) }))}
            onRefresh={() => setReloadKey((key) => key + 1)}
          />
          <Tabs value={tab} onChange={(_event, value: KioskReportTab) => setTab(value)} variant="scrollable" allowScrollButtonsMobile>
            {KIOSK_REPORT_TABS.map((id) => (
              <Tab key={id} value={id} label={translate(`reports.kiosk.tabs.${id}`)} />
            ))}
          </Tabs>
        </Box>
      }
      exportButtons={{ onExportCSV: handleExport, exporting, disabled: report === null }}
      loading={loading}
      error={error}
    >
      {report !== null && report.tab === 'payout' && <PayoutTable report={report.body} />}
      {report !== null && report.tab === 'daily' && <DailyMatchTable report={report.body} />}
      {report !== null && report.tab === 'refunds' && <RefundsHealthTable report={report.body} />}
      {report !== null && report.tab === 'overview' && <OverviewTable report={report.body} />}
    </ReportLayout>
  );
};
